import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { FolderIcon, UploadIcon } from 'lucide-react';

import { MIN_LEN } from '@/constants';
import { useClientSixDetailFileBrowser } from '@/hooks/sixDetail/useClientSixDetailFileBrowser';
import type { UseSixDetailPageReturn } from '@/hooks/sixDetail/useSixDetailPage.types';
import { useSixDetailUploadProgress } from '@/hooks/sixDetail/useSixDetailUploadProgress';
import { useAppDispatch, useAppSelector } from '@/store';
import { mapParticipantsToParticipantCards } from '@/store/mappers/mapSixDetail';
import { selectCurrentUserId } from '@/store/selectors/auth';
import { selectConnectionStatusBySixId } from '@/store/selectors/connection';
import { selectCatalogHasLoaded, selectCatalogSixes } from '@/store/selectors/sixCatalog';
import { selectActivity, selectFiles, selectFileSortDir, selectFileSortKey, selectFolderBreadcrumb, selectParticipants, selectSearchQuery, selectSelectedFileIds, selectSixDetailActiveFilters, selectSixDetailMeta } from '@/store/selectors/sixDetail';
import { navigateToBreadcrumb, navigateToFolder, navigateToRoot, setActiveFilters, setSelectedFileIds, setFileSort, setSearchQuery, openFileDetailModal, resetSixDetailState } from '@/store/slices/sixDetail/sixDetailSlice';
import { deleteFileThunk } from '@/store/thunks/sixDetail/deleteFileThunk';
import { deleteFolderThunk } from '@/store/thunks/sixDetail/deleteFolderThunk';
import { useMoveItems } from '@/hooks/sixDetail/useMoveItems';
import { exportActivityThunk } from '@/store/thunks/sixDetail/exportActivityThunk';
import { fetchActivityThunk } from '@/store/thunks/sixDetail/fetchActivityThunk';
import { fetchFilesThunk } from '@/store/thunks/sixDetail/fetchFilesThunk';
import { initSixDetailThunk } from '@/store/thunks/sixDetail/getInitialDetailThunk';
import { searchFilesThunk } from '@/store/thunks/sixDetail/searchFilesThunk';
import { syncFilesFromApiThunk } from '@/store/thunks/sixDetail/syncFilesFromApiThunk';
import { toggleFileFavoriteThunk } from '@/store/thunks/sixDetail/toggleFileFavoriteThunk';
import { showManageAccessModal, showFileDetailModal } from '@/store/thunks/ui/uiThunk';
import { useActivityCards, useBreadcrumbItems } from '@/hooks/sixDetail/useSixDetailDerivedState';
import type { DownloadFilterKey, FileEntry, FileSortDir, FileSortKey, ParticipantCardModel } from '@/types/SixDetail.types';
import { getDownloadedSixDetailFilePayload } from '@/utils/download.utils';
import { saveBlobToDiskThunk } from '@/store/thunks/files/saveBlobToDiskThunk';
import { getLastActivityLabel, getMembersCount } from '@/utils/sixDetail.utils';
import { resolveCanDeleteSixFile, resolveCanDeleteSixFolder } from '@/utils/permissions/sixPermissions';
import { saveFilesToDirectory, type SaveableFile } from '@/utils/saveFilesToDirectory.utils';
import { showErrorToast, showSuccessToast } from '@/utils/toast.utils';

const ACTIVITY_POLL_INTERVAL_MS = 15_000; const SEARCH_DEBOUNCE_MS = 300;

export function useSixDetailPage(sixId: string): UseSixDetailPageReturn {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const navigate = useNavigate();
  const allFiles = useAppSelector((state) => state.sixDetail.files);
  const files = useAppSelector(selectFiles);
  const activities = useAppSelector(selectActivity);
  const participants = useAppSelector(selectParticipants);
  const sixDetailMeta = useAppSelector(selectSixDetailMeta);
  const folderBreadcrumb = useAppSelector(selectFolderBreadcrumb);
  const reduxSearchQuery = useAppSelector(selectSearchQuery);
  const [localSearchQuery, setLocalSearchQuery] = useState(reduxSearchQuery ?? '');
  const [downloadFilterKey, setDownloadFilterKey] = useState<DownloadFilterKey>('allFiles');
  const selectedFileIds = useAppSelector(selectSelectedFileIds);
  const sortKey = useAppSelector(selectFileSortKey);
  const sortDir = useAppSelector(selectFileSortDir);
  const activeFilters = useAppSelector(selectSixDetailActiveFilters);
  const currentUserId = useAppSelector(selectCurrentUserId);
  const connectionStatusMap = useAppSelector(selectConnectionStatusBySixId);
  const connectionStatus = connectionStatusMap[sixId] ?? 'disconnected';
  const catalogHasLoaded = useAppSelector(selectCatalogHasLoaded);
  const catalogSixes = useAppSelector(selectCatalogSixes);
  const uploadProgress = useSixDetailUploadProgress(sixId);
  const dispatch = useAppDispatch();
  const searchTimerRef = useRef<ReturnType<typeof setTimeout>>(); const lastConnectedSyncKeyRef = useRef(''); const hasRedirectedMissingSixRef = useRef(false);
  useEffect(() => {
    if (typeof sixId !== 'string' || sixId.trim().length < MIN_LEN) return;
    void dispatch(initSixDetailThunk(sixId));
  }, [dispatch, sixId]);
  useEffect(() => {
    if (typeof sixId !== 'string' || sixId.trim().length < MIN_LEN) return;
    const intervalId = setInterval(() => {
      void dispatch(fetchActivityThunk(sixId));
    }, ACTIVITY_POLL_INTERVAL_MS);
    return () => {
      clearInterval(intervalId);
    };
  }, [dispatch, sixId]);

  const syncedFileKey = useMemo(
    () => allFiles
      .filter((item) => item.kind === 'file' && typeof item.cid === 'string' && item.cid.trim().length > 0)
      .map((item) => `${item.id}:${item.cid}`)
      .join('|'),
    [allFiles],
  );
  useEffect(() => {
    if (connectionStatus !== 'connected') {
      lastConnectedSyncKeyRef.current = '';
      return;
    }
    if (typeof sixId !== 'string' || sixId.trim().length < MIN_LEN) return;
    if (syncedFileKey.length === 0) {
      lastConnectedSyncKeyRef.current = '';
      return;
    }
    if (lastConnectedSyncKeyRef.current === syncedFileKey) return;

    lastConnectedSyncKeyRef.current = syncedFileKey;
    void dispatch(syncFilesFromApiThunk({ sixId, files: allFiles }));
  }, [allFiles, connectionStatus, dispatch, sixId, syncedFileKey]);

  useEffect(
    () => () => {
      if (searchTimerRef.current !== undefined) {
        clearTimeout(searchTimerRef.current);
      }
    },
    [],
  );
  const isCurrentSixInCatalog = useMemo(
    () => catalogSixes.some((catalogSix) => catalogSix.id === sixId),
    [catalogSixes, sixId],
  );

  useEffect(() => {
    if (typeof sixId !== 'string' || sixId.trim().length < MIN_LEN) {
      return;
    }
    if (!catalogHasLoaded) {
      return;
    }
    if (isCurrentSixInCatalog) {
      hasRedirectedMissingSixRef.current = false;
      return;
    }
    if (hasRedirectedMissingSixRef.current) {
      return;
    }

    hasRedirectedMissingSixRef.current = true;
    dispatch(resetSixDetailState());
    void navigate({ to: '/app/sixes', replace: true });
  }, [catalogHasLoaded, dispatch, isCurrentSixInCatalog, navigate, sixId]);

  const hasActiveClientQuery = useMemo(
    () => (
      localSearchQuery.trim().length > MIN_LEN ||
      activeFilters.length > MIN_LEN ||
      downloadFilterKey !== 'allFiles'
    ),
    [activeFilters.length, downloadFilterKey, localSearchQuery],
  );

  const browserSourceItems = useMemo(
    () => (hasActiveClientQuery ? allFiles : files),
    [allFiles, files, hasActiveClientQuery],
  );

  const visibleFiles = useClientSixDetailFileBrowser({
    items: browserSourceItems,
    searchQuery: localSearchQuery,
    activeFilters,
    downloadFilterKey,
    sortKey,
    sortDir,
    currentUserId,
  });

  const sixName = sixDetailMeta?.name ?? ''; const sixDescription = sixDetailMeta?.description ?? '';
  const totalFilesCount = sixDetailMeta?.fileCount ?? allFiles.filter((item) => item.kind === 'file').length;
  const handleNavigateToDashboard = useCallback((): void => {
    void navigate({ to: '/app/sixes' });
  }, [navigate]);
  const handleFolderOpen = useCallback(
    (item: Pick<FileEntry, 'path' | 'isNavigationEntry'>): void => {
      if (hasActiveClientQuery) {
        if (searchTimerRef.current !== undefined) {
          clearTimeout(searchTimerRef.current);
        }
        setLocalSearchQuery('');
        setDownloadFilterKey('allFiles');
        dispatch(setActiveFilters([]));
        dispatch(setSearchQuery(undefined));
      }

      const folderPath = item.path;
      if (item.isNavigationEntry === true && folderPath === undefined) {
        dispatch(navigateToRoot());
        return;
      }
      if (typeof folderPath !== 'string' || folderPath.length === 0) return;
      dispatch(navigateToFolder(folderPath));
    },
    [dispatch, hasActiveClientQuery],
  );

  const handleBreadcrumbNavigate = useCallback(
    (breadcrumbPath: string): void => {
      dispatch(navigateToBreadcrumb(breadcrumbPath));
    },
    [dispatch],
  );

  const handleNavigateToRoot = useCallback((): void => {
    dispatch(navigateToRoot());
  }, [dispatch]);
  const handleSearchChange = useCallback(
    (query: string): void => {
      setLocalSearchQuery(query);

      if (searchTimerRef.current !== undefined) {
        clearTimeout(searchTimerRef.current);
      }

      searchTimerRef.current = setTimeout(() => {
        void dispatch(searchFilesThunk(query));
      }, SEARCH_DEBOUNCE_MS);
    },
    [dispatch],
  );

  const handleSelectionChange = useCallback(
    (ids: string[]): void => {
      dispatch(setSelectedFileIds(ids));
    },
    [dispatch],
  );

  const handleClearSelection = useCallback((): void => {
    dispatch(setSelectedFileIds([]));
  }, [dispatch]);
  const handleDownloadFilterChange = useCallback((filterKey: DownloadFilterKey): void => {
    setDownloadFilterKey(filterKey);
  }, []);
  const handleSortChange = useCallback(
    (nextSortKey: FileSortKey, nextSortDir: FileSortDir): void => {
      dispatch(setFileSort({ sortKey: nextSortKey, sortDir: nextSortDir }));
      if (typeof sixId !== 'string' || sixId.trim().length < MIN_LEN) return;
      void dispatch(fetchFilesThunk({ sixId }));
    },
    [dispatch, sixId],
  );

  const handleBulkDownload = useCallback((): void => {
    if (typeof sixId !== 'string' || sixId.trim().length < MIN_LEN) return;
    const filesInView = visibleFiles.filter((item) => item.isNavigationEntry !== true);
    const filesToDownload = filesInView.filter(
      (item) => selectedFileIds.includes(item.id) && item.kind === 'file',
    );
    if (filesToDownload.length === 0) return;

    const downloadSequentially = async(): Promise<void> => {
      const downloadedFiles: SaveableFile[] = [];

      for (const item of filesToDownload) {
        const filePayload = await getDownloadedSixDetailFilePayload({
          item,
          currentUserId,
          dispatch,
          sixId,
        });
        if (filePayload !== undefined) {
          downloadedFiles.push(filePayload);
        }
      }
      if (downloadedFiles.length === 0) return;
      const saveResult = await saveFilesToDirectory(downloadedFiles);
      if (saveResult === 'saved') {
        showSuccessToast(
          downloadedFiles.length === 1
            ? 'File downloaded successfully'
            : `${downloadedFiles.length} files downloaded successfully`,
        );
        return;
      }

      if (saveResult === 'unsupported') {
        showErrorToast('Bulk download requires a browser that supports folder selection');
      }
    };

    void downloadSequentially();
  }, [currentUserId, dispatch, selectedFileIds, sixId, visibleFiles]);

  const handleBulkDelete = useCallback((): void => {
    if (typeof sixId !== 'string' || sixId.trim().length < MIN_LEN) return;

    const filesInView = visibleFiles.filter((item) => item.isNavigationEntry !== true);
    const fileIds = filesInView
      .filter((item) => selectedFileIds.includes(item.id) && item.kind === 'file')
      .map((item) => item.id);

    if (fileIds.length === 0) return;

    const deleteSequentially = async(): Promise<void> => {
      for (const fileId of fileIds) {
        await dispatch(deleteFileThunk({ sixId, fileId })).unwrap();
      }
    };

    void deleteSequentially()
      .then(() => {
        showSuccessToast(
          fileIds.length === 1
            ? 'File deleted successfully'
            : `${fileIds.length} files deleted successfully`,
        );
      })
      .catch(() => {
        showErrorToast('Failed to delete file');
      });
  }, [dispatch, selectedFileIds, sixId, visibleFiles]);

  const handleMenuItemClick = useCallback((): void => {
    // Placeholder for upload / create folder modal
  }, []);

  const handleMenuClick = useCallback((): void => {
    setIsMenuOpen((previous) => !previous);
  }, []);

  const handleExportActivity = useCallback((): void => {
    if (typeof sixId !== 'string' || sixId.trim().length < MIN_LEN) return;
    void (async() => {
      const resultAction = await dispatch(exportActivityThunk(sixId));
      if (exportActivityThunk.fulfilled.match(resultAction)) {
        void dispatch(saveBlobToDiskThunk({ blob: resultAction.payload, filename: `activity-export-${sixId}.csv` }));
      }
    })();
  }, [dispatch, sixId]);

  const handleViewFileDetail = useCallback(
    (fileId: string): void => {
      if (typeof sixId !== 'string' || sixId.trim().length < MIN_LEN) return;
      dispatch(openFileDetailModal(fileId));
      dispatch(showFileDetailModal());
    },
    [dispatch, sixId],
  );

  const handleToggleFileFavorite = useCallback(
    (fileId: string): void => {
      if (typeof sixId !== 'string' || sixId.trim().length < MIN_LEN) return;
      void dispatch(toggleFileFavoriteThunk({ sixId, fileId }));
    },
    [dispatch, sixId],
  );
  const canDeleteFile = useCallback(
    (item: FileEntry): boolean => item.kind === 'file' && resolveCanDeleteSixFile(
      currentUserId,
      participants?.owners,
      item.uploadedByUserId,
    ),
    [currentUserId, participants?.owners],
  );
  const canDeleteFolder = useCallback((folderPath: string | undefined): boolean => resolveCanDeleteSixFolder(
    currentUserId,
    participants?.owners,
    allFiles,
    folderPath,
  ), [allFiles, currentUserId, participants?.owners]);

  const handleDeleteFile = useCallback(
    (item: FileEntry): void => {
      if (item.kind !== 'file') return;
      if (typeof sixId !== 'string' || sixId.trim().length < MIN_LEN) return;
      if (!canDeleteFile(item)) {
        showErrorToast('You do not have permission to delete this file');
        return;
      }

      void dispatch(deleteFileThunk({ sixId, fileId: item.id }))
        .unwrap()
        .then(() => {
          showSuccessToast('File deleted successfully');
        })
        .catch(() => {
          showErrorToast('Failed to delete file');
        });
    },
    [canDeleteFile, dispatch, sixId],
  );

  const handleDeleteFolder = useCallback(
    (id: string, folderPath: string): void => {
      if (typeof sixId !== 'string' || sixId.trim().length < MIN_LEN) return;
      if (id.trim().length === 0) return;
      console.log("checking if can delete ", canDeleteFolder(id))
      if (!canDeleteFolder(id)) {
        showErrorToast('You do not have permission to delete this folder');
        return;
      }
      void dispatch(deleteFolderThunk({ sixId, id, folderPath }))
        .unwrap()
        .then(() => {
          showSuccessToast('Folder deleted successfully');
        })
        .catch(() => {
          showErrorToast('Failed to delete folder');
        });
    },
    [canDeleteFolder, dispatch, sixId],
  );

  const handleDownloadFile = useCallback(
    (item: FileEntry): void => {
      void (async() => {
        const filePayload = await getDownloadedSixDetailFilePayload({
          item,
          currentUserId,
          dispatch,
          sixId,
        });
        if (filePayload === undefined) return;
        void dispatch(saveBlobToDiskThunk({ blob: filePayload.blob, filename: filePayload.filename }));
      })();
    },
    [currentUserId, dispatch, sixId],
  );

  const handleItemAction = useCallback(
    (item: FileEntry, action: string): void => {
      if (action === 'open' && item.kind === 'folder') {
        handleFolderOpen(item);
        return;
      }

      if (action === 'view') {
        handleViewFileDetail(item.id);
        return;
      }

      if (action === 'favorite' && item.kind === 'file') {
        handleToggleFileFavorite(item.id);
        return;
      }

      if (action === 'download' && item.kind === 'file') {
        handleDownloadFile(item);
        return;
      }

      if (action === 'delete' && item.kind === 'file') {
        handleDeleteFile(item);
        return;
      }

      if (action === 'delete' && item.kind === 'folder') {
        handleDeleteFolder(item.id, item.path ?? '');
      }
    },
    [
      handleDeleteFile,
      handleDeleteFolder,
      handleDownloadFile,
      handleFolderOpen,
      handleToggleFileFavorite,
      handleViewFileDetail,
    ],
  );

  const handleManageAccessClick = useCallback((): void => {
    if (typeof sixId !== 'string' || sixId.trim().length < MIN_LEN) return;
    void dispatch(showManageAccessModal(sixId));
  }, [dispatch, sixId]);

  const menuItems = useMemo(
    () => [
      {
        label: 'New Folder',
        icon: <FolderIcon className="h-4 w-4" />,
        onClick: handleMenuItemClick,
      },
      {
        label: 'Upload File',
        icon: <UploadIcon className="h-4 w-4" />,
        onClick: handleMenuItemClick,
      },
    ],
    [handleMenuItemClick],
  );
  const membersCount = getMembersCount(participants); const lastActivityLabel = useMemo(() => getLastActivityLabel(activities), [activities]);

  const participantCards = useMemo<ParticipantCardModel[]>(
    () => mapParticipantsToParticipantCards(participants),
    [participants],
  );
  const canManageAdministrativeActions = useMemo(() => {
    if (typeof currentUserId !== 'string' || currentUserId.trim().length === 0) {
      return false;
    }

    if (participants === undefined) {
      return false;
    }

    const isOwner = participants.owners.some((participant) => participant.id === currentUserId);

    return isOwner;
  }, [currentUserId, participants]);

  const activityCards = useActivityCards(activities);
  const { handleMoveItems } = useMoveItems(sixId, allFiles);
  const breadcrumbItems = useBreadcrumbItems(folderBreadcrumb, handleNavigateToRoot, handleBreadcrumbNavigate);

  return {
    state: {
      sixName,
      sixDescription,
      connectionStatus,
      files: visibleFiles,
      showPathColumn: hasActiveClientQuery,
      totalFilesCount,
      canManageAdministrativeActions,
      searchQuery: localSearchQuery,
      downloadFilterKey,
      selectedFileIds,
      sortKey,
      sortDir,
      isMenuOpen,
      menuItems,
      membersCount,
      lastActivityLabel,
      participantCards,
      activityCards,
      breadcrumbItems,
      inFlightUploads: uploadProgress.state.inFlightUploads,
    },
    handlers: {
      setIsMenuOpen,
      handleNavigateToDashboard,
      handleSearchChange,
      handleDownloadFilterChange,
      handleSelectionChange,
      handleClearSelection,
      handleSortChange,
      handleBulkDownload,
      handleBulkDelete,
      handleMenuClick,
      handleManageAccessClick,
      handleExportActivity,
      handleItemAction,
      handleUploadRetry: uploadProgress.handlers.handleUploadRetry,
      handleUploadProgressComplete: uploadProgress.handlers.handleUploadProgressComplete,
      handleMoveItems,
    },
  };
}
